import Dexie from "dexie";
import { db } from "./schema";
import type {
  BookmarkRow,
  CardRow,
  CoverRow,
  DeckColorConfig,
  DeckRow,
  DetectedCloze,
  PdfRow,
  QuestionRow,
  Qtype,
  Rect,
  ReviewRow,
} from "../types";
import { cardKey, correspondCards } from "../sync/cardKeys";
import { setActiveStars, type StarMap } from "../sync/progressMerge";

export interface ImportParams {
  name: string;
  blob: Blob;
  pageCount: number;
  pageW: number;
  pageH: number;
  color: DeckColorConfig;
  clozes: DetectedCloze[];
  /** Stable cross-device id for the sync registry (generated by the caller on import). */
  bookId?: string;
}

function clozesToCards(
  clozes: DetectedCloze[],
  deckId: number,
  pdfId: number,
  now: number,
): CardRow[] {
  return clozes.map((cz) => ({
    deckId,
    pdfId,
    pageIndex: cz.pageIndex,
    rects: cz.rects,
    answerRect: cz.bbox,
    text: cz.text,
    // createdAt doubles as the cloze's LWW timestamp (P0-2): a sync-materialize passes the merged `t`
    // to preserve it; a fresh manual add / re-detect leaves it unset and stamps "now".
    createdAt: cz.t ?? now,
  }));
}

export async function importDeck(p: ImportParams): Promise<number> {
  const now = Date.now();
  return db.transaction("rw", db.decks, db.pdfs, db.cards, async () => {
    const deckId = (await db.decks.add({
      name: p.name,
      createdAt: now,
      color: p.color,
      bookId: p.bookId,
    })) as number;
    const pdfId = (await db.pdfs.add({
      deckId,
      name: p.name,
      blob: p.blob,
      pageCount: p.pageCount,
      pageW: p.pageW,
      pageH: p.pageH,
    })) as number;
    const cards = clozesToCards(p.clozes, deckId, pdfId, now);
    for (let i = 0; i < cards.length; i += 500) {
      await db.cards.bulkAdd(cards.slice(i, i + 500));
    }
    return deckId;
  });
}

export function listDecks(): Promise<DeckRow[]> {
  return db.decks.orderBy("createdAt").reverse().toArray();
}

export function getDeck(deckId: number): Promise<DeckRow | undefined> {
  return db.decks.get(deckId);
}

export function getDeckPdf(deckId: number): Promise<PdfRow | undefined> {
  return db.pdfs.where("deckId").equals(deckId).first();
}

export function answerCount(deckId: number): Promise<number> {
  return db.cards.where("deckId").equals(deckId).count();
}

/** Add a manual answer mask on a page (user fixing a detection miss). Returns the new card id. */
export async function addCard(
  deckId: number,
  pdfId: number,
  pageIndex: number,
  rect: Rect,
): Promise<number> {
  return (await db.cards.add({
    deckId,
    pdfId,
    pageIndex,
    rects: [rect],
    answerRect: rect,
    text: "",
    createdAt: Date.now(),
  })) as number;
}

/** Remove a single answer mask (user fixing a false positive). */
export async function deleteCard(cardId: number): Promise<void> {
  await db.cards.delete(cardId);
}

export async function updateDeck(deckId: number, patch: Partial<DeckRow>): Promise<void> {
  await db.decks.update(deckId, patch);
}

/** Replace a deck's cards with `clozes` and carry ★/revealed over to the new cards by geometric
 * overlap (re-detection makes NEW ids and may move answers; §4.4). Runs INSIDE an open rw
 * transaction; returns the ★ patch for the caller's deck.update + the old/new cloze key sets. */
async function replaceCardsCarryStars(
  deckId: number,
  pdfId: number,
  deck: DeckRow | undefined,
  clozes: DetectedCloze[],
  now: number,
): Promise<{
  count: number;
  oldKeys: Set<string>;
  newKeys: Set<string>;
  patch: Pick<DeckRow, "starred" | "revealed" | "starsLww">;
}> {
  const oldCards = await db.cards.where("deckId").equals(deckId).toArray();
  await db.cards.where("deckId").equals(deckId).delete();
  const cards = clozesToCards(clozes, deckId, pdfId, now);
  const newIds: number[] = [];
  for (let i = 0; i < cards.length; i += 500) {
    const keys = await db.cards.bulkAdd(cards.slice(i, i + 500), { allKeys: true });
    newIds.push(...(keys as number[]));
  }
  const newCards = cards.map((c, i) => ({
    id: newIds[i],
    pageIndex: c.pageIndex,
    answerRect: c.answerRect,
  }));
  const corr = correspondCards(oldCards, newCards);
  const remap = (ids?: number[]) =>
    (ids ?? []).map((id) => corr.get(id)).filter((x): x is number => x != null);
  const newStarred = remap(deck?.starred);
  const newRevealed = remap(deck?.revealed);
  // Re-anchor the ★ LWW map to the carried-over stars' position keys (stale keys -> inert tombstones).
  const byId = new Map(newCards.map((c) => [c.id, c] as const));
  const starMap: StarMap = { ...(deck?.starsLww ?? {}) };
  setActiveStars(
    starMap,
    newStarred.map((id) => cardKey(byId.get(id)!.pageIndex, byId.get(id)!.answerRect)),
    now,
  );
  return {
    count: cards.length,
    oldKeys: new Set(oldCards.map((c) => cardKey(c.pageIndex, c.answerRect))),
    newKeys: new Set(newCards.map((c) => cardKey(c.pageIndex, c.answerRect))),
    patch: { starred: newStarred, revealed: newRevealed, starsLww: starMap },
  };
}

/** Re-run detection for a deck under a new color config: replace all its answers (carrying ★ over),
 * and tombstone the masks the re-detect removed so the deletion propagates cross-device (P0-2/§4.4). */
export async function redetectDeck(
  deckId: number,
  color: DeckColorConfig,
  clozes: DetectedCloze[],
): Promise<number> {
  const now = Date.now();
  return db.transaction("rw", db.decks, db.pdfs, db.cards, async () => {
    const pdf = await db.pdfs.where("deckId").equals(deckId).first();
    if (!pdf?.id) throw new Error("PDFが見つかりません");
    const deck = await db.decks.get(deckId);
    const r = await replaceCardsCarryStars(deckId, pdf.id, deck, clozes, now);
    const tomb: Record<string, number> = { ...(deck?.clozeTomb ?? {}) };
    for (const k of r.oldKeys) if (!r.newKeys.has(k)) tomb[k] = now; // removed -> tombstone
    for (const k of r.newKeys) delete tomb[k]; // re-detected position is live again
    await db.decks.update(deckId, { color, ...r.patch, clozeTomb: tomb });
    return r.count;
  });
}

/** Materialize the server-MERGED mask set (P0-2): replace cards with the active clozes (preserving
 * their merged `t` via cloze.t) and adopt the merged tombstones. Used by refreshContent. */
export async function materializeContent(
  deckId: number,
  color: DeckColorConfig,
  active: DetectedCloze[],
  tombs: Record<string, number>,
): Promise<void> {
  const now = Date.now();
  await db.transaction("rw", db.decks, db.pdfs, db.cards, async () => {
    const pdf = await db.pdfs.where("deckId").equals(deckId).first();
    if (!pdf?.id) throw new Error("PDFが見つかりません");
    const deck = await db.decks.get(deckId);
    const r = await replaceCardsCarryStars(deckId, pdf.id, deck, active, now);
    await db.decks.update(deckId, { color, ...r.patch, clozeTomb: tombs });
  });
}

/** Lowest page index that has any answer (so the viewer/preview opens usefully). */
export async function firstAnswerPage(deckId: number): Promise<number> {
  const c = await db.cards
    .where("[deckId+pageIndex]")
    .between([deckId, Dexie.minKey], [deckId, Dexie.maxKey], true, true)
    .first();
  return c?.pageIndex ?? 0;
}

/** All answers on a given page (for the red-sheet viewer). */
export function cardsOnPage(deckId: number, pageIndex: number): Promise<CardRow[]> {
  return db.cards
    .where("[deckId+pageIndex]")
    .between([deckId, pageIndex], [deckId, pageIndex], true, true)
    .toArray();
}

/** All answers in a deck (grouped by page in the viewer). */
export function deckCards(deckId: number): Promise<CardRow[]> {
  return db.cards.where("deckId").equals(deckId).toArray();
}

export async function deleteDeck(deckId: number): Promise<void> {
  await db.transaction("rw", [db.decks, db.pdfs, db.cards, db.bookmarks, db.covers], async () => {
    await db.cards.where("deckId").equals(deckId).delete();
    await db.bookmarks.where("deckId").equals(deckId).delete();
    await db.pdfs.where("deckId").equals(deckId).delete();
    await db.covers.delete(deckId);
    await db.decks.delete(deckId);
  });
}

// ---- cover thumbnails (cached page-1 render, regenerable) ----

export function getCover(deckId: number): Promise<CoverRow | undefined> {
  return db.covers.get(deckId);
}

export async function setCover(deckId: number, blob: Blob): Promise<void> {
  await db.covers.put({ deckId, blob });
}

// ---- bookmarks (the user-built 目次) ----

export async function addBookmark(deckId: number, pageIndex: number, title: string): Promise<void> {
  await db.bookmarks.add({ deckId, pageIndex, title, createdAt: Date.now() });
}

/** Bulk-add bookmarks (e.g. imported from a PDF's built-in outline). */
export async function importBookmarks(
  deckId: number,
  items: { title: string; pageIndex: number }[],
): Promise<void> {
  if (!items.length) return;
  const now = Date.now();
  await db.bookmarks.bulkAdd(
    items.map((b) => ({ deckId, pageIndex: b.pageIndex, title: b.title, createdAt: now })),
  );
}

export function listBookmarks(deckId: number): Promise<BookmarkRow[]> {
  return db.bookmarks
    .where("[deckId+pageIndex]")
    .between([deckId, Dexie.minKey], [deckId, Dexie.maxKey], true, true)
    .toArray();
}

export async function renameBookmark(id: number, title: string): Promise<void> {
  await db.bookmarks.update(id, { title });
}

/** Replace ALL of a deck's bookmarks (used when pulling synced bookmarks from the cloud). */
export async function replaceBookmarks(
  deckId: number,
  items: { title: string; pageIndex: number }[],
): Promise<void> {
  const now = Date.now();
  await db.transaction("rw", db.bookmarks, async () => {
    await db.bookmarks.where("deckId").equals(deckId).delete();
    if (items.length)
      await db.bookmarks.bulkAdd(
        items.map((b) => ({ deckId, pageIndex: b.pageIndex, title: b.title, createdAt: now })),
      );
  });
}

export async function deleteBookmark(id: number): Promise<void> {
  await db.bookmarks.delete(id);
}

// ---- AI-generated questions (keyed by the cross-device bookId) ----

/** Replace one (page × type) question group (initial generation or regeneration). The other
 * type's questions on the same page are untouched. Reviews of the replaced ids go with them. */
export async function savePageQuestions(
  bookId: string,
  pageIndex: number,
  qtype: Qtype,
  qs: QuestionRow[],
): Promise<void> {
  await db.transaction("rw", db.questions, db.reviews, async () => {
    const old = await db.questions
      .where("[bookId+pageIndex]")
      .equals([bookId, pageIndex])
      .filter((q) => q.qtype === qtype)
      .toArray();
    await db.questions.bulkDelete(old.map((q) => q.id));
    await db.reviews.bulkDelete(old.map((q) => q.id));
    if (qs.length) await db.questions.bulkPut(qs);
  });
}

/** Delete one (page × type) question group + its reviews (問題一覧の削除). */
export async function deleteQuestionGroup(
  bookId: string,
  pageIndex: number,
  qtype: Qtype,
): Promise<void> {
  await savePageQuestions(bookId, pageIndex, qtype, []);
}

/** Questions for one page. */
export function getPageQuestions(bookId: string, pageIndex: number): Promise<QuestionRow[]> {
  return db.questions.where("[bookId+pageIndex]").equals([bookId, pageIndex]).toArray();
}

/** All questions in a book (the quiz set). */
export function getBookQuestions(bookId: string): Promise<QuestionRow[]> {
  return db.questions.where("bookId").equals(bookId).toArray();
}

/** Replace ALL of a book's questions (cloud restore for Pro+). Reviews of ids that no longer
 * exist are pruned so a regeneration on another device can't leave orphaned SM-2 state. */
export async function putBookQuestions(bookId: string, qs: QuestionRow[]): Promise<void> {
  await db.transaction("rw", db.questions, db.reviews, async () => {
    await db.questions.where("bookId").equals(bookId).delete();
    if (qs.length) await db.questions.bulkPut(qs);
    const live = new Set(qs.map((q) => q.id));
    const stale = (await db.reviews.where("bookId").equals(bookId).toArray())
      .filter((r) => !live.has(r.questionId))
      .map((r) => r.questionId);
    if (stale.length) await db.reviews.bulkDelete(stale);
  });
}

/** Delete a book's questions + reviews (on book delete). */
export async function deleteBookQuestions(bookId: string): Promise<void> {
  await db.transaction("rw", db.questions, db.reviews, async () => {
    await db.questions.where("bookId").equals(bookId).delete();
    await db.reviews.where("bookId").equals(bookId).delete();
  });
}

// ---- SM-2 review records (機能拡張 §A-2/§D — all plans record locally) ----

/** All review records for a book, as a questionId-keyed map. */
export async function getBookReviews(bookId: string): Promise<Map<string, ReviewRow>> {
  const rows = await db.reviews.where("bookId").equals(bookId).toArray();
  return new Map(rows.map((r) => [r.questionId, r]));
}

/** Upsert one review record (after an answer, or from a cloud pull). */
export async function putReview(row: ReviewRow): Promise<void> {
  await db.reviews.put(row);
}

/** All review records (cross-book — drives the 今日の復習 card + sync push). */
export function allReviews(): Promise<ReviewRow[]> {
  return db.reviews.toArray();
}

/** Due review records across all books, most-overdue first (今日の復習). */
export async function dueReviews(now: number): Promise<ReviewRow[]> {
  return db.reviews.where("dueAt").belowOrEqual(now).sortBy("dueAt");
}

/** Questions by id (cross-book session assembly). Missing ids are dropped. */
export async function questionsByIds(ids: string[]): Promise<QuestionRow[]> {
  const rows = await db.questions.bulkGet(ids);
  return rows.filter((q): q is QuestionRow => !!q);
}
